import { decodeJwt } from "jose";
import { defineStore } from "pinia";

import api from "@/api";
import { refreshTokens } from "@/api/utils";
import { useUserStore } from "./user";

const REFRESH_TIME_REMAINING = 300;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    loading: false,
    accessToken: undefined,
    refreshToken: undefined,
    accessExpires: 0,
    loadedFromCookies: false,
  }),
  actions: {
    login(tokens) {
      this.setTokens(tokens);
    },
    async isLoggedIn() {
      if (!this._hasTokens()) {
        await this._loadTokensFromCookies();
      }

      // has access token
      if (!!this.accessToken) {
        // access token has not expired
        if (!this.isTokenExpired(this.accessToken)) {
          return true;
        }
      }

      this.clearAccessToken();

      // has refresh token
      if (!!this.refreshToken) {
        // get new tokens
        await this._refreshTokens();
      }

      // check for presence of access token
      return !!this.accessToken;
    },
    async _loadTokensFromCookies() {
      if (this.loadedFromCookies) {
        return;
      }

      const cookies = await window.cookieStore.getAll();
      this.clearTokens();
      cookies.forEach((cookie) => {
        if (cookie.name === "access_token") {
          this.accessToken = cookie.value;
        }
        else if (cookie.name === "refresh_token") {
          this.refreshToken = cookie.value;
        }
      });

      this.loadedFromCookies = true;
    },
    _hasTokens() {
      return (
        !!this.accessToken &&
        !!this.refreshToken
      );
    },
    getTokenExpires(accessToken) {
      const claims = decodeJwt(accessToken);
      return claims.exp;
    },
    isTokenExpired(accessToken) {
      const timeRemaining = this.getTokenExpires(accessToken) - (Date.now() / 1000);
      //console.log("time remaining before refreshing tokens", timeRemaining - REFRESH_TIME_REMAINING);
      return timeRemaining <= REFRESH_TIME_REMAINING;
    },
    async _refreshTokens() {
      await navigator.locks.request("refreshEzeeTokens", async (lock) => {
        if (!this._hasTokens()) {
          await this._loadTokensFromCookies();
        }

        const accessToken = this.accessToken;
        if (!accessToken || this.isTokenExpired(accessToken)) {
          this.clearAccessToken();

          // no refresh token, nothing we can do
          if (!this.refreshToken) {
            return;
          }

          const response = await refreshTokens();
          if (response.ok) {
            const body = await response.json();
            this.setTokens(body.tokens);
          }
          else {
            this.clearTokens();
          }
        }
      });
    },
    async getTokens() {
      await this._refreshTokens();
      return {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
      }
    },
    setTokens(tokens) {
      this.accessToken = tokens.access_token;
      this.refreshToken = tokens.refresh_token;
      this.accessExpires = this.getTokenExpires(tokens.access_token);
    },
    clearAccessToken() {
      this.accessToken = undefined;
      this.accessExpires = 0;
    },
    clearTokens() {
      this.clearAccessToken();
      this.refreshToken = undefined;
    },
    async logout() {
      await api.auth.logout();
      this.clearTokens();
      window.location.href = "/auth/login";
      document.title = "EZee Assist";
    },
    async checkAuth() {
      try {
        //console.log('Checking auth');
        this.loading = true;

        // Call the backend endpoint to check authentication status
        const response = await api.auth.checkAuth(false);
        this.setTokens(response.tokens);

        return true;
      } catch (error) {
        console.error('Auth is not valid. User is *NOT* logged in');
        this.clearTokens();
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
});
